/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  IAssetAudio,
  IAssetBase,
  IAssetCubeTexture,
  IAssetModel,
  IAssetPointCloud,
  IAssetTexture,
  IAssetVideo,
  loadAudio,
  loadCubeTexture,
  loadModel,
  loadPointCloud,
  loadTexture,
  loadVideo,
  unloadAudio,
  unloadCubeTexture,
  unloadModel,
  unloadPointCloud,
  unloadTexture,
  unloadVideo,
} from "../core/loader/loader";
import AppBabylon from "./app.babylon";

//----------------------------------------------------------------
export class AppContentConfig {
  public loadBatchSize?: number;
}

//----------------------------------------------------------------
export default class AppContent extends AppBabylon {
  protected contentConfig: AppContentConfig = new AppContentConfig();
  protected assets: IAssetBase[] = [];
  protected assetsLoading: IAssetBase[] = [];

  constructor(renderCanvas: HTMLCanvasElement) {
    super(renderCanvas);
  }

  public setConfigContent(contentConfig: AppContentConfig) {
    this.contentConfig = contentConfig;
  }

  //----------------------------------------------------------------
  public async loadContents(assetsToLoad: IAssetBase[]) {
    // check if any assets are already loading.
    // this is a work around the batching feature,
    // making sure assets can be removed from the async for-loop,
    // and are not loaded after being unloaded.
    for (const assetLoading of this.assetsLoading) {
      for (const assetToLoad of assetsToLoad) {
        if (assetLoading.path === assetToLoad.path) {
          const asssetToLoadIdx = assetsToLoad.indexOf(assetToLoad);
          assetsToLoad.splice(asssetToLoadIdx, 1);
        }
      }
    }
    this.assetsLoading.push(...assetsToLoad);
    // load assets.
    const batchSize = this.contentConfig.loadBatchSize;
    if (batchSize) {
      while (this.assetsLoading.length > 0) {
        const numToLoad = Math.min(batchSize, this.assetsLoading.length);
        const batch = this.assetsLoading.splice(0, numToLoad);
        await this.loadAssets(batch);
      }
    } else {
      const numToLoad = this.assetsLoading.length; // load all assets at once.
      const batch = this.assetsLoading.splice(0, numToLoad);
      await this.loadAssets(batch);
    }
  }

  public unloadContents(assetsToUnload: IAssetBase[]) {
    // if any assets are waiting to be loaded,
    // remove them form the loading list.
    for (const assetLoading of this.assetsLoading) {
      for (const assetToUnload of assetsToUnload) {
        if (assetLoading.path === assetToUnload.path) {
          const assetLoadingIdx = this.assetsLoading.indexOf(assetLoading);
          this.assetsLoading.splice(assetLoadingIdx, 1);
        }
      }
    }
    this.unloadAssets(assetsToUnload);
  }

  //----------------------------------------------------------------
  protected async loadAssets(assetsToLoad: IAssetBase[]) {
    const promises = assetsToLoad.map(assetToLoad => this.loadAsset(assetToLoad));
    await Promise.all(promises);
  }

  protected async loadAsset(assetToLoad: IAssetBase) {
    const assetIdx = this.assets.findIndex(asset => asset.path === assetToLoad.path);
    if (assetIdx >= 0) {
      console.error("app.content.loadAsset - asset already exists: ", assetToLoad.path);
      return;
    }
    const asset: IAssetBase = {
      type: assetToLoad.type,
      path: assetToLoad.path,
      id: assetToLoad.id || assetToLoad.path, // if no unique id is provided, use path as unique id.
    };
    this.assets.push(asset);

    asset.loaded = false;
    asset.loading = true;
    asset.loadProgress = 0;

    // TODO: should assets that fail remain in this.assets array or be removed?

    if (asset.type === "model") {
      const assetModel = asset as IAssetModel;
      assetModel.extension = (assetToLoad as IAssetModel).extension; // copy extension from assetToLoad.
      await loadModel(assetModel, this.scene!, this.rootNode!);
    } else if (asset.type === "pointcloud") {
      const assetPointCloud = asset as IAssetPointCloud;
      assetPointCloud.size = (assetToLoad as IAssetPointCloud).size; // copy size from assetToLoad.
      assetPointCloud.color = (assetToLoad as IAssetPointCloud).color; // copy color from assetToLoad.
      await loadPointCloud(assetPointCloud, this.scene!, this.rootNode!);
    } else if (asset.type === "texture") {
      const assetTexture = asset as IAssetTexture;
      await loadTexture(assetTexture, this.scene!);
    } else if (asset.type === "cubetexture") {
      const assetCubeTexture = asset as IAssetCubeTexture;
      await loadCubeTexture(assetCubeTexture, this.scene!);
    } else if (asset.type === "video") {
      const assetVideo = asset as IAssetVideo;
      assetVideo.videoElement = (assetToLoad as IAssetVideo).videoElement; // copy videoElement from assetToLoad.
      await loadVideo(assetVideo, this.scene!);
    } else if (asset.type === "audio") {
      const assetAudio = asset as IAssetAudio;
      assetAudio.audioElement = (assetToLoad as IAssetAudio).audioElement; // copy audioElement from assetToLoad.
      await loadAudio(assetAudio, this.scene!);
    }

    asset.loaded = true;
    asset.loading = false;
    asset.loadProgress = 1;

    this.loadAssetComplete(asset);
  }

  protected loadAssetComplete(_asset: IAssetBase) {
    // override in subclass.
  }

  //----------------------------------------------------------------
  protected unloadAssets(assetsToUnload: IAssetBase[]) {
    for (const assetToUnload of assetsToUnload) {
      this.unloadAsset(assetToUnload);
    }
  }

  protected unloadAsset(assetToUnload: IAssetBase) {
    const assetIdx = this.assets.findIndex(asset => asset.path === assetToUnload.path);
    if (assetIdx < 0) {
      console.error("app.content.unloadAsset - asset does not exists: ", assetToUnload.path);
      return;
    }
    const asset = this.assets[assetIdx];
    if (asset.type === "model") {
      const assetModel = asset as IAssetModel;
      unloadModel(assetModel);
    } else if (asset.type === "pointcloud") {
      const assetPointCloud = asset as IAssetPointCloud;
      unloadPointCloud(assetPointCloud);
    } else if (asset.type === "texture") {
      const assetTexture = asset as IAssetTexture;
      unloadTexture(assetTexture);
    } else if (asset.type === "cubetexture") {
      const assetCubeTexture = asset as IAssetCubeTexture;
      unloadCubeTexture(assetCubeTexture);
    } else if (asset.type === "video") {
      const assetVideo = asset as IAssetVideo;
      unloadVideo(assetVideo);
    } else if (asset.type === "audio") {
      const assetAudio = asset as IAssetAudio;
      unloadAudio(assetAudio);
    }
    this.assets.splice(assetIdx, 1);
  }
}
